import { useEffect, useMemo, useState } from "react";

import {
    Button,
    Divider,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Select,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { HttpStatusCode } from "axios";
import { Field, FieldProps, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST } from "@/common/constants";
import CKEditorComponent from "@/components/Fields/CKEditorComponent";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { emailTemplateSchema } from "@/schemas/emailTemplateSchema";
import { getEmailTemplateById, postEmailTemplate, putEmailTemplate } from "@/services/emailTemplateService";
import { EMAIL_TYPE_OPTIONS } from "@/data/emailType";

const EditEmailTemplate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isOpenPreview,
        onOpen: onOpenPreview,
        onClose: onClosePreview,
    } = useDisclosure();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);

    const { id } = location.state || {};

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [confirmationText, setConfirmationText] = useState("");
    const [confirmButtonText, setConfirmButtonText] = useState("");
    const [cancelButtonText, setCancelButtonText] = useState("");
    const [dataDetail, setDataDetail] = useState<any>(null);
    const [dataEdit, setDataEdit] = useState<any>(null);

    const initialValues = useMemo(() => ({
        emailType: dataDetail?.emailType ?? '',
        emailTypeName: dataDetail?.emailTypeName ?? '',
        emailTemplateFileName: dataDetail?.emailTemplateFileName ?? '',
        emailTemplateHtmlContent: dataDetail?.emailTemplateHtmlContent ?? '',
        description: dataDetail?.description ?? '',
    }), [dataDetail]);

    const getDataDetail = async () => {
        const response = await getEmailTemplateById(id) as any;
        if (response.status === HttpStatusCode.Ok) {
            setDataDetail(response.data);
        } else {
            toast({
                title: 'Failed to load data.',
                description: response?.message || 'Unable to load template data',
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id]);

    const handleCancel = () => {
        setTitle("Cancel Changes?");
        setConfirmationText("Are you sure you want to discard all changes?");
        setConfirmButtonText("Yes");
        setCancelButtonText("No");
        onOpen();
    };

    const handleSubmit = (data: any) => {
        setTitle("Save Template?");
        setConfirmationText("Are you sure you want to save this template?");
        setConfirmButtonText("Save");
        setCancelButtonText("Cancel");
        setDataEdit(data);
        setIsSubmitting(true);
        onOpen();
    };

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel Changes?") {
            navigate('/settings/email-templates');
            return;
        }

        const payload = {
            ...(id ? { id } : {}),
            emailType: Number(dataEdit.emailType),
            emailTypeName: dataEdit.emailTypeName,
            emailTemplateFileName: dataEdit.emailTemplateFileName,
            emailTemplateHtmlContent: dataEdit.emailTemplateHtmlContent,
            description: dataEdit.description,
            ...(id
                ? { modifiedBy: name || 'System' }
                : { createdBy: name || 'System' }),
        };

        const response = id
            ? await putEmailTemplate(payload)
            : await postEmailTemplate(payload);

        setIsSubmitting(false);
        if (response?.status === HttpStatusCode.Ok) {
            toast({
                title: 'Template saved.',
                description: "We've saved this template.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setTimeout(() => {
                navigate('/settings/email-templates');
            }, 700);
        } else {
            toast({
                title: 'Template save failed.',
                description: response?.message || 'We were unable to save this template.',
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    };

    return (
        <>
            <ModalConfirmation
                isOpen={isOpen}
                onClose={onClose}
                title={title}
                handleCancel={() => {
                    setIsSubmitting(false);
                    onClose();
                }}
                handleConfirm={handleConfirm}
                confirmationText={confirmationText}
                confirmButtonText={confirmButtonText}
                cancelButtonText={cancelButtonText}
            />

            <Modal onClose={onClosePreview} isOpen={isOpenPreview} size='6xl' isCentered>
                <ModalOverlay />
                <ModalContent borderRadius={15} padding={2}>
                    <ModalHeader>Template Preview</ModalHeader>
                    <ModalCloseButton _focus={{ outline: 'none', boxShadow: 'none' }} />
                    <ModalBody>
                        <iframe
                            title='email-template-preview-editor'
                            srcDoc={dataEdit?.emailTemplateHtmlContent || initialValues.emailTemplateHtmlContent || '<p>No preview available</p>'}
                            style={{ width: '100%', minHeight: 520, border: '1px solid #EAECF0', borderRadius: 8 }}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>

            <div className="form-container">
                <Formik
                    initialValues={initialValues}
                    validationSchema={emailTemplateSchema}
                    onSubmit={(value) => {
                        handleSubmit(value);
                    }}
                    enableReinitialize
                >
                    {(props) => (
                        <Form>
                            <Field name='emailType'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.emailType && form.touched.emailType)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Email Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Select
                                                    {...field}
                                                    placeholder='Select Email Type'
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        const selectedValue = Number(e.target.value);
                                                        const option = EMAIL_TYPE_OPTIONS.find((item) => item.value === selectedValue);
                                                        props.setFieldValue('emailType', selectedValue);
                                                        if (!id) {
                                                            props.setFieldValue('emailTypeName', option?.label || '');
                                                        }
                                                    }}
                                                    isDisabled={Boolean(id)}
                                                >
                                                    {EMAIL_TYPE_OPTIONS.map((item) => (
                                                        <option key={item.value} value={item.value}>{item.label}</option>
                                                    ))}
                                                </Select>
                                                <FormErrorMessage>{String(form.errors.emailType)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='emailTypeName'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.emailTypeName && form.touched.emailTypeName)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Email Type Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input Email Type Name' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.emailTypeName)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='emailTemplateFileName'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.emailTemplateFileName && form.touched.emailTemplateFileName)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Template File Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input Template File Name' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.emailTemplateFileName)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='description'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.description && form.touched.description)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Description</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input Description' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.description)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='emailTemplateHtmlContent'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.emailTemplateHtmlContent && form.touched.emailTemplateHtmlContent)} style={{ marginBottom: 40 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">HTML Content</FormLabel>
                                            <div className="form-input-container">
                                                <CKEditorComponent
                                                    value={props.values.emailTemplateHtmlContent || ''}
                                                    onChange={(value) => props.setFieldValue('emailTemplateHtmlContent', value)}
                                                    isDisabled={isSubmitting}
                                                />
                                                <FormErrorMessage style={{ marginTop: '30px' }}>{String(form.errors.emailTemplateHtmlContent)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    marginRight={5}
                                    onClick={() => {
                                        setDataEdit(props.values);
                                        onOpenPreview();
                                    }}
                                >
                                    Preview
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Save Template
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
};

export default EditEmailTemplate;
